import { useEffect, useState } from 'react';
import {
  ServiceRequestFormContextValue,
  useServiceRequestFormContext,
} from 'src/components/ServiceRequestForm/CustomHooks/ServiceRequestFormContext';
import { useUnitAreaAssets } from 'src/services/api/UnitAreaAssets/hooks/useUnitAreaAssets';
import { useWarrantyExpiredAssets } from 'src/SharedCustomHooks/UseWarrantyExpiredAssets/useWarrantyExpiredAssets';
import { onUrlImageExtensionExist } from 'src/helpers/CommonHelpers';
import { ServiceRequestFormStep } from 'src/components/ServiceRequestForm/CustomHooks/serviceRequestFormSteps';

export interface UseServiceRequestFormAssetOrchestratorReturn {
  formData: any;
  user: any;
  assets: any[];
  diagnosisAsset: string;
  diagnosisIssueType: string;
  warrantyExpiresAt: string;
  expandedRoom: string;
  hasExpiredAssets: boolean;
  floorplanUrl: string;
  hasViewableFloorplan: boolean;
  isLightboxOpen: boolean;
  nextButtonLabel: string;
  isCheckingFaqs: boolean;
  canProceed: boolean;
  patchFormData: (data: any) => void;
  loadFaqsForAsset: (assetItemId: string) => Promise<void>;
  onToggleRoom: (room: any) => void;
  onViewFloorplan: () => void;
  closeLightbox: () => void;
  onNext: () => void;
  onBack: () => void;
  onExit: () => void;
}

export const useServiceRequestFormAssetOrchestrator =
  (): UseServiceRequestFormAssetOrchestratorReturn => {
    const {
      formData,
      patchFormData,
      goNext,
      goPrev,
      goToStep,
      user,
      onExit,
      reference,
    }: ServiceRequestFormContextValue = useServiceRequestFormContext();

    const { issueTypes, faqs, isLoadingFaqs, loadFaqsForAsset } = reference;

    const { unitAreaAssets: assets, fetchData: fetchAssets } =
      useUnitAreaAssets();

    const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);

    useEffect(() => {
      if (
        !formData.issueType &&
        !formData.issueTypeId &&
        issueTypes.length > 0
      ) {
        const general: any = issueTypes.find(
          (type: any) => type.name === 'General'
        );

        if (general) {
          patchFormData({ issueType: general.name, issueTypeId: general.id });
        }
      }

      fetchAssets(formData.suiteId);
    }, []);

    const allRoomAssets: any[] = assets.flatMap(
      (room: any) => room.assets ?? []
    );

    const { hasExpiredAssets } = useWarrantyExpiredAssets(allRoomAssets);

    const isHomeOwner: boolean = user?.roles?.[0]?.role?.name === 'Home Owner';

    const hasFaqs: boolean = faqs.length > 0;

    const floorplanUrl: string = formData.suiteFloorPLan;

    const hasFloorplan: boolean = Boolean(floorplanUrl && floorplanUrl.length > 0);

    const isFloorplanImage: boolean =
      hasFloorplan && onUrlImageExtensionExist(floorplanUrl) === true;

    const nextButtonLabel: string = isHomeOwner
      ? hasFaqs
        ? 'Next: Common Questions'
        : 'Next: Add Details'
      : 'Next: Identify Issue';

    const onViewFloorplan = (): void => {
      if (isFloorplanImage) {
        setIsLightboxOpen(true);
        return;
      }

      window.open(floorplanUrl, '_blank');
    };

    const closeLightbox = (): void => {
      setIsLightboxOpen(false);
    };

    const onToggleRoom = (room: any): void => {
      patchFormData({
        viewAsset: formData.viewAsset === room.name ? '' : room.name,
      });
    };

    const onNext = (): void => {
      if (isHomeOwner) {
        goToStep(
          hasFaqs ? ServiceRequestFormStep.Category : ServiceRequestFormStep.Details
        );
        return;
      }

      goNext();
    };

    const onBack = (): void => {
      goPrev();
    };

    return {
      formData,
      user,
      assets,
      diagnosisAsset: formData.asset_item_nameDescription,
      diagnosisIssueType: isHomeOwner
        ? formData.issueType
        : formData.asset_item_type,
      warrantyExpiresAt: formData.assetWarrantyExpiresAt,
      expandedRoom: formData.viewAsset,
      hasExpiredAssets,
      floorplanUrl,
      hasViewableFloorplan: hasFloorplan,
      isLightboxOpen,
      nextButtonLabel,
      isCheckingFaqs: isLoadingFaqs,
      canProceed: Boolean(formData.asset_item_id),
      patchFormData,
      loadFaqsForAsset,
      onToggleRoom,
      onViewFloorplan,
      closeLightbox,
      onNext,
      onBack,
      onExit,
    };
  };